import { apiClient, ApiError } from './client.js';
import { devError } from '../lib/devLog.js';
import { API_ENDPOINTS } from './endpoints.js';
import { DATE_ONLY_REGEX } from '../constants/booking.js';
import type { 
  Service, 
  ServiceCategory,
  GetAvailabilityDto, 
  AvailabilityResponse, 
  AppointmentResponse,
  ExternalContact,
} from '@minibook/shared';

// Расширенный тип для услуги с категорией (из ответа API)
export interface ServiceWithCategory extends Service {
  category: ServiceCategory | null;
}

// Тип публичного профиля мастера
export interface PublicProfile {
  id: string;
  name: string;
  timeZone: string; // Timezone мастера для корректного отображения времени
  publicTitle: string;
  description: string | null;
  coverPhoto: string | null;
  address: string | null;
  phone: string | null;
  socialLinks: ExternalContact[];
  messengerLinks: ExternalContact[];
  vkUrl: string | null;
  tgUsername: string | null;
  /** Имя бота приложения для кнопки «Написать боту» (управление записью), не от мастера */
  telegramBotUsername?: string | null;
  waLink: string | null;
  max?: string | null;
  workHours: Array<{
    weekday: number;
    intervals: Array<{ start_min: number; end_min: number }>;
  }>;
  /** Каналы уведомлений клиенту, подключённые у мастера (из БД). Показываем галочки только если >= 2. */
  availableNotifyChannels?: Array<'email' | 'vk' | 'telegram'>;
  theme?: 'light' | 'dark' | 'pink' | 'olive';
  locale?: 'ru' | 'en' | 'uz' | 'tg' | 'ky' | 'az' | 'hy' | 'kk' | 'hi' | 'it' | 'ro';
  onlineBookingEnabled?: boolean;
  enabled?: boolean; // fallback: some backends may expose enabled instead of onlineBookingEnabled
}

export type PublicClientChannelKey = 'email' | 'vk' | 'telegram';
export type PublicOptionalClientChannelKey = 'vk' | 'telegram';

export interface PublicClientChannelState {
  channel: PublicClientChannelKey;
  available: boolean;
  connected: boolean;
  enabled: boolean;
  connectUrl: string | null;
}

// Товар мастера для публичного букинга
export interface PublicProduct {
  id: string;
  name: string;
  price: number;
  description?: string | null;
  category?: { id: string; name: string; sortOrder: number } | null;
}

// Контекст шага 3: подключённые каналы клиента по телефону (для выбора одного канала)
export interface BookingContextDto {
  connectedChannels: Array<'email' | 'vk' | 'telegram'>;
  preferredChannel: 'email' | 'vk' | 'telegram' | null;
  clientEmail: string | null;
}

// DTO создания записи: один канал (notifyChannel) или устаревший notifyChannels
export interface CreateAppointmentDto {
  serviceIds: string[];
  productIds?: string[];
  startAt: string; // ISO datetime
  clientName: string;
  clientPhone: string;
  clientEmail?: string | null;
  comment?: string;
  notifyChannel?: 'email' | 'vk' | 'telegram';
  notifyChannels?: Array<'email' | 'vk' | 'telegram'>;
  vkUserId?: string | null;
  gender?: 'male' | 'female' | null;
  birthDate?: string | null; // ISO date YYYY-MM-DD
}

// Публичные данные пользователя VK (для автозаполнения формы)
export interface VkUserPublicDto {
  firstName?: string;
  lastName?: string;
  photo?: string;
  sex?: 1 | 2; // 1 — женский, 2 — мужской
  birthDate?: string; // "DD.MM" или "DD.MM.YYYY"
}

// Расширенный ответ с manageToken
export interface AppointmentWithManageToken extends AppointmentResponse {
  manageToken?: string;
  manageShortCode?: string;
}

export interface CalendarAvailabilityResponse {
  availableDates: string[];
  /** Доля занятости по дням 0..1 (опционально, для заливки в календаре) */
  occupancyByDate?: Record<string, number>;
  requestId?: string;
}

export interface ManageLinkResolution {
  appointmentId: string;
  token: string;
  masterSlug: string;
}

export interface ManageAppointmentDetails {
  appointment: AppointmentResponse & {
    serviceIds?: string[];
    orderedServices?: Array<{
      id: string;
      name: string;
      durationMin: number;
      price: number;
      orderIndex?: number;
    }>;
  };
  clientEmail?: string | null;
  clientChannels: PublicClientChannelState[];
  master: {
    id: string;
    name: string;
    timeZone: string;
    publicTitle: string | null;
    address: string | null;
    phone: string | null;
    socialLinks: ExternalContact[];
    messengerLinks: ExternalContact[];
    vkUrl: string | null;
    tgUsername: string | null;
    waLink: string | null;
    max?: string | null;
  };
}

export interface UpdateManageChannelsPayload {
  channels: Array<{ channel: PublicClientChannelKey; enabled: boolean }>;
}

// Получить услуги мастера для онлайн-записи
export async function getPublicServices(slug: string): Promise<ServiceWithCategory[]> {
  return apiClient.get<ServiceWithCategory[]>(API_ENDPOINTS.publicServices(slug));
}

// Получить товары мастера для онлайн-записи
export async function getPublicProducts(slug: string): Promise<PublicProduct[]> {
  return apiClient.get<PublicProduct[]>(API_ENDPOINTS.publicProducts(slug));
}

// Получить доступные слоты
export async function getAvailability(
  slug: string, 
  payload: GetAvailabilityDto,
  options?: { signal?: AbortSignal }
): Promise<AvailabilityResponse> {
  try {
    if (!payload.date || !DATE_ONLY_REGEX.test(payload.date)) {
      throw {
        error: {
          code: 'validation_error',
          message: `Неверный формат даты: ${payload.date}. Ожидается YYYY-MM-DD`,
        },
      } as ApiError;
    }

    const queryParams = new URLSearchParams({ date: payload.date });
    if (payload.serviceIds.length > 0) {
      queryParams.set('serviceIds', payload.serviceIds.join(','));
    }
    const url = `${API_ENDPOINTS.publicAvailability(slug)}?${queryParams.toString()}`;
    return await apiClient.get<AvailabilityResponse>(url, {
      signal: options?.signal,
      cache: 'no-store',
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      devError('[getAvailability] Ошибка:', error);
    }
    throw error as ApiError;
  }
}

export async function getCalendarAvailability(
  slug: string,
  params: { from: string; to: string; serviceIds?: string[] }
): Promise<CalendarAvailabilityResponse> {
  try {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    if (params.serviceIds?.length) {
      query.set('serviceIds', params.serviceIds.join(','));
    }
    const url = `${API_ENDPOINTS.publicCalendarAvailability(slug)}?${query.toString()}`;
    return await apiClient.get<CalendarAvailabilityResponse>(url, {
      cache: 'no-store',
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      devError('[getCalendarAvailability] Ошибка:', error);
    }
    throw error as ApiError;
  }
}

// Получить публичный профиль мастера
export async function getPublicProfile(slug: string): Promise<PublicProfile> {
  return apiClient.get<PublicProfile>(API_ENDPOINTS.publicProfile(slug));
}

// Проверка телефона в базе мастера (только безопасный сигнал существования)
export interface CheckPhoneResponse {
  exists: boolean;
}
export async function checkPhoneDuplicate(slug: string, phone: string): Promise<CheckPhoneResponse> {
  return apiClient.get<CheckPhoneResponse>(API_ENDPOINTS.publicCheckPhone(slug)(phone));
}

export async function getBookingContext(slug: string, phone: string | null | undefined): Promise<BookingContextDto> {
  const url = API_ENDPOINTS.publicBookingContext(slug)(phone ?? '');
  return apiClient.get<BookingContextDto>(url);
}

// Клиент по VK ID в базе мастера (только безопасный сигнал существования)
export interface ClientByVkResponse {
  exists: boolean;
}
export async function getClientByVk(slug: string, vkUserId: string): Promise<ClientByVkResponse> {
  return apiClient.get<ClientByVkResponse>(API_ENDPOINTS.publicClientByVk(slug, vkUserId));
}

// Получить публичные данные пользователя VK (для автозаполнения формы записи)
export async function getVkUserPublic(slug: string, vkUserId: string): Promise<VkUserPublicDto> {
  return apiClient.get<VkUserPublicDto>(API_ENDPOINTS.publicVkUser(slug, vkUserId));
}

// Разрешить shortCode в appointmentId + token
export async function resolveManageLinkByShortCode(
  shortCode: string
): Promise<ManageLinkResolution> {
  return apiClient.get<ManageLinkResolution>(
    API_ENDPOINTS.publicManageByShortCode(shortCode)
  );
}

// Получить данные записи для управления
export async function getManageAppointment(
  appointmentId: string,
  token: string
): Promise<ManageAppointmentDetails> {
  const url = `${API_ENDPOINTS.publicManageAppointment(appointmentId)}?token=${encodeURIComponent(token)}`;
  return apiClient.get<ManageAppointmentDetails>(url);
}

export async function updateManageAppointmentChannels(
  manageToken: string,
  payload: UpdateManageChannelsPayload
): Promise<{ clientChannels: PublicClientChannelState[]; clientEmail: string | null }> {
  return apiClient.patch<{ clientChannels: PublicClientChannelState[]; clientEmail: string | null }>(
    API_ENDPOINTS.publicManageChannels(manageToken),
    payload
  );
}

// Создать запись через публичный API
export async function createPublicAppointment(
  slug: string,
  payload: CreateAppointmentDto
): Promise<AppointmentWithManageToken> {
  return apiClient.post<AppointmentWithManageToken>(API_ENDPOINTS.publicAppointments(slug), payload);
}

// Отменить запись
export async function cancelAppointment(
  appointmentId: string,
  token: string
): Promise<{ success: boolean }> {
  return apiClient.post<{ success: boolean }>(
    API_ENDPOINTS.cancelAppointment(appointmentId),
    { token }
  );
}

// Перенести запись
export async function rescheduleAppointment(
  appointmentId: string,
  token: string,
  startAt: string
): Promise<{ success: boolean }> {
  return apiClient.post<{ success: boolean }>(
    API_ENDPOINTS.rescheduleAppointment(appointmentId),
    { token, startAt }
  );
}
